/**
 * Generic class to handle in-memory search on arbitrary items.
 *
 * New items can be added dynamically.
 * Items are generically typed and the extractor determines how to interpret them.
 *
 * Search terms must be strings, but result can be anything.
 * However, not that there is an advantage to using results that are comparable
 * with `===` when using the `uniqueValueOptimization` option.
 *
 * ## Use cases
 *
 * ### Tag search
 *
 * Items with tags can be searched by treating the `searchTerm` as the tag and
 * adding each item to multiple terms. E.g. if item an item `Carrot` has the
 * tags `food` and `plant` and the item `Cow` has the tags `food` and `animal`,
 * tag search can be setup by
 * ```
 * const map = new SearchMap()
 * map.addTerm("food", Carrot)
 * map.addTerm("food", Cow)
 * map.addTerm("plant", Carrot)
 * map.addTerm("animal", Cow)
 * ```
 *
 * To optimize this, the extractor could have its signature changed to let it
 * add multiple entries per item. This improvement is listed below under
 * _Future work_.
 *
 * ### Title search
 *
 * This is the simplest form of search, which involves finding an item or value
 * by full or partial match.
 * It is built like tag search, but usually limited to one result per search
 * term, and the extractor can be used to add items easily.
 *
 * An extractor is defined to do the work of splitting the input items into
 * search term and value.
 *
 * ## Future work
 *
 * Going forward, this class will be expanded as needed. The following are features we highly expect will be relevant in
 * the future.
 *
 * - Removing items.
 * - Removing terms (will remove multiple result entries).
 * - Make it possible for the extractor to add multiple entries per item.
 * - Optional extractor.
 * - Index access for exact matches. Perhaps configurable to be for partial instead.
 */
export class SearchMap<ItemType, ResultType> {
  /**
   * Maps search terms to result arrays.
   * Each added term will have at least one entry of the result type.
   */
  private resultMap: Map<string, ResultType[]>
  private readonly extractor: (item: ItemType) => [{ term: string; value: ResultType }]
  private readonly caseSensitive: boolean
  private readonly uniqueValueOptimization: boolean

  /**
   * Constructs a new Search Map, by setting it up with an extractor.
   *
   * @param extractor The extractor extracts the term (the exact string matched on exact match search) and the result
   * from the data items. This way it defines which part of the items is the result we are looking for (it could be the
   * whole item or just a part of it), and which part we use to define the search term string that will yield the
   * result(s) on a hit.
   * @param caseSensitive Whether search is case-sensitive.
   * @param uniqueValueOptimization Optimize by only keeping one of each result in each result arrays under each term.
   * Enabling this makes {@link addItem} idempotent - i.e. adding the same result for the same term twice will make no
   * changes to the search map the second time.
   * Note that result values are compared with `===`, so adding two identical objects (e.g. by making a shallow copy of
   * an object and adding them both under the same term) will add them both to the result array.
   * @param dataItems An iterable with the initial data items to base the search on.
   */
  constructor(
    extractor: (item: ItemType) => [{ term: string; value: ResultType }],
    { caseSensitive = false, uniqueValueOptimization = false } = {},
    dataItems?: Iterable<ItemType>
  ) {
    this.resultMap = new Map<string, ResultType[]>()
    this.extractor = extractor
    this.caseSensitive = caseSensitive
    this.uniqueValueOptimization = uniqueValueOptimization

    if (dataItems) {
      for (const dataItem of dataItems) {
        this.addItem(dataItem)
      }
    }
  }

  //region Properties

  /**
   * Get the number of added terms
   */
  get termCount() {
    return this.resultMap.size
  }

  //endregion

  //region Adding

  /**
   * Add a specific term to the map.
   *
   * @param searchTerm The term to add.
   * @param value The value to add under the term.
   */
  addTerm(searchTerm: string, value: ResultType) {
    if (!this.caseSensitive) {
      searchTerm = searchTerm.toLowerCase()
    }

    // When the result map does not have the term...
    if (!this.resultMap.has(searchTerm)) {
      // ...add it with the current value...
      this.resultMap.set(searchTerm, [value])
    } else {
      // ...otherwise, if...
      const termResults = this.resultMap.get(searchTerm)
      if (
        termResults && // (Fool typing because `Map.get()` has return type `V | undefined` - it will always be an array here).
        (!this.uniqueValueOptimization || // ...unique optimization is disabled, or...
          !termResults.includes(value)) // ...the term result array does not already include the value...
      ) {
        // ...add the value
        termResults.push(value)
      }
    }
  }

  /**
   * Adds an item to the map.
   *
   * @param item The item to extract and add.
   */
  addItem(item: ItemType) {
    const entries = this.extractor(item)
    for (const entry of entries) {
      this.addTerm(entry.term, entry.value)
    }
  }

  //endregion

  //region Querying

  /**
   * Search for an exact match of a term.
   *
   * E.g. if the map contains the terms "Alpha", "Bravo" and "Charlie", results can be yielded for each of these terms
   * and no results can be yielded for any other terms.
   *
   * @param searchTerm The term to search for.
   * @param maxResults Maximum number of yielded results.
   */
  getExactMatches(searchTerm: string, maxResults?: number): ResultType[] {
    if (!this.caseSensitive) {
      searchTerm = searchTerm.toLowerCase()
    }
    if (this.resultMap.has(searchTerm)) {
      const result = this.resultMap.get(searchTerm)
      // Slice on too many results
      if (result && maxResults && result.length > maxResults) {
        return result.slice(0, maxResults)
      }
      return result || []
    } else {
      return []
    }
  }

  /**
   * Like {@link getExactMatches}, but returns undefined on no matches.
   * Useful if you want to test whether the result is truthy or falsy, as empty arrays are truthy.
   *
   * @param searchTerm The term to search for.
   * @param maxResults Maximum number of yielded results.
   */
  getExactMatchesOrUndefined(searchTerm: string, maxResults?: number): ResultType[] | undefined {
    const result = this.getExactMatches(searchTerm, maxResults)
    return result.length === 0 ? undefined : result
  }

  /**
   * Search for a partial match of a term.
   *
   * E.g. if the map contains the terms "Alpha", "Bravo" and "Charlie", searching for the term "ha" can yield the
   * concatenated results from both "Alpha" and "Charlie".
   *
   * @param searchTerm The term to search for.
   * @param maxResults Maximum number of yielded results.
   */
  getPartialMatches(searchTerm: string, maxResults?: number): ResultType[] {
    if (!this.caseSensitive) {
      searchTerm = searchTerm.toLowerCase()
    }

    // Note: We could use a set here to de-duplicate, but that would not work for object result types.
    //   Furthermore, we do not expect the same result to show up under two different terms.
    //   - JMK, 2021-06-21
    const result: ResultType[] = []

    searchLoop: for (const key of this.resultMap.keys()) {
      // Using the Map key iterator to avoid filling memory by constructing arrays
      if (key.includes(searchTerm)) {
        // Term is valid. Add results to final result.
        const keyResults = this.resultMap.get(key) || [] // Last part to avoid typing complaining about possibly undefined
        for (const keyResult of keyResults) {
          result.push(keyResult)
          if (result.length === maxResults) {
            break searchLoop
          }
        }
      }
    }

    return result
  }

  /**
   * Like {@link getPartialMatches}, but returns undefined on no matches.
   * Useful if you want to test whether the result is truthy or falsy, as empty arrays are truthy.
   *
   * @param searchTerm The term to search for.
   * @param maxResults Maximum number of yielded results.
   */
  getPartialMatchesOrUndefined(searchTerm: string, maxResults?: number): ResultType[] | undefined {
    const result = this.getPartialMatches(searchTerm, maxResults)
    return result.length === 0 ? undefined : result
  }

  //endregion
}
